import React, { useRef, useState } from 'react'
import { Box, Typography, ListItem, Collapse, IconButton, Button } from '@mui/material'
import { useTheme, styled, Theme } from '@mui/material/styles'
import { useAlertStatus } from '../hooks/useAlertStatus'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import murmurhash from 'murmurhash'
import getConditionLabel from '../../../common/utils/getConditionLabel'
import { ConditionGroupType, ConditionType } from '../../builder/contexts/PatternTreeContext'
import { Pattern } from '../../builder/contexts/PatternContext'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import TriggerChartDetailsInput from '../../builder/components/TriggerChartDetailsInput'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import { useCopyPatternToAccount } from '../../../hooks/useCopyPatternToAccount'
import { isPatternOwner } from '../../../common/utils/isPatternOwner'

interface PatternConditionReadViewProps {
  pattern: Pattern
  isActive?: boolean
  liveStatus?: boolean
}

// Bring a just-expanded section into view so the user sees what opened
const revealExpanded = (el: HTMLElement | null) => {
  el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

// Helper function to sort object keys for consistent hashing
const sortObjectKeys = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(sortObjectKeys)

  return Object.keys(obj)
    .sort()
    .reduce((result: any, key) => {
      result[key] = sortObjectKeys(obj[key])
      return result
    }, {})
}

// Helper function to get timeframe conversion
const getTimeframeConversion = (timeframe: string): string => {
  switch (timeframe) {
    case 'M1':
      return 'min'
    case 'M5':
      return 'min'
    case 'M15':
      return 'min'
    case 'M30':
      return 'min'
    case 'H1':
      return 'hour'
    case 'H4':
      return 'hour'
    case 'D1':
      return 'day'
    case 'W1':
      return 'week'
    default:
      return timeframe
  }
}

// Styled components for tree structure
const ConditionContainer = styled(Box)<{}>(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  paddingLeft: 0,
  paddingTop: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(-1),
    top: theme.spacing(-2),
    bottom: 0,
    width: 2,
    height: 'auto',
    backgroundColor: theme.palette.divider,
  },
}))

const GroupContainer = styled(Box)<{}>(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  paddingLeft: 0,
  paddingTop: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(-1),
    top: theme.spacing(-2),
    bottom: 0,
    width: 2,
    height: 'auto',
    minHeight: '50px',
    backgroundColor: theme.palette.divider,
  },
}))

const ConditionItemContainer = styled(Box)<{}>(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: `calc(${theme.spacing(-1)} + 2px)`,
    top: theme.spacing(2),
    width: theme.spacing(2),
    height: 2,
    backgroundColor: theme.palette.divider,
    zIndex: 1,
  },
  '&:last-child': {
    marginBottom: 0,
  },
}))

const GroupItemContainer = styled(Box)<{}>(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: `calc(${theme.spacing(-1)} + 2px)`,
    top: theme.spacing(2),
    width: theme.spacing(2),
    height: 2,
    backgroundColor: theme.palette.divider,
    zIndex: 1,
  },
  '&:last-child': {
    marginBottom: 0,
  },
}))

// Component to render a single condition
const ConditionItem: React.FC<{
  condition: ConditionType
  isActive?: boolean
  alertActive?: boolean
  liveStatus: boolean
  canViewPatternDetails?: boolean
}> = ({ condition, isActive, alertActive, liveStatus, canViewPatternDetails }) => {
  const theme = useTheme()

  return (
    <ConditionItemContainer>
      <ListItem
        sx={{
          py: 1,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        {liveStatus && (
          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
            {!alertActive || !canViewPatternDetails ? (
              <PauseCircleOutlineIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
            ) : isActive ? (
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
            )}
          </Box>
        )}
        <Box sx={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" mr={1}>
            {canViewPatternDetails ? getConditionLabel(condition) : 'Upgrade to view'}
          </Typography>
        </Box>
      </ListItem>
    </ConditionItemContainer>
  )
}

// Component to recursively render condition groups
export const ConditionGroupItem: React.FC<{
  symbol: string
  group: ConditionGroupType
  statuses?: { [key: string]: boolean }
  level?: number
  alertActive?: boolean
  liveStatus: boolean
  isRoot?: boolean
  canViewPatternDetails?: boolean
}> = ({
  symbol,
  group,
  statuses = {},
  level = 0,
  alertActive = false,
  liveStatus,
  isRoot = false,
  canViewPatternDetails,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate group status
  const getGroupStatus = (group: ConditionGroupType): { isActive: boolean; isPartial: boolean } => {
    if (!liveStatus) return { isActive: false, isPartial: false }

    let activeCount = 0
    let hasPartial = false

    // Check conditions
    for (const condition of group.conditions) {
      // Create hashable condition for status lookup
      const hashableCondition = {
        chartDetails: condition.chartDetails,
        settings: condition.settings,
        condition: condition.condition,
      }

      // Generate hash for condition status lookup
      const conditionHash = murmurhash
        .v3(JSON.stringify(sortObjectKeys(hashableCondition)))
        .toString()

      if (statuses[conditionHash]) {
        activeCount++
      }
    }

    // Check nested groups recursively
    for (const nestedGroup of group.conditionGroups) {
      const nestedStatus = getGroupStatus(nestedGroup)
      if (nestedStatus.isActive) {
        activeCount++
      } else if (nestedStatus.isPartial) {
        hasPartial = true
      }
    }

    return {
      isActive: activeCount >= group.count && group.count > 0,
      isPartial: (activeCount > 0 || hasPartial) && activeCount < group.count,
    }
  }

  const groupStatus = getGroupStatus(group)

  // Render status icon based on group status
  const renderStatusIcon = () => {
    if (!alertActive || !canViewPatternDetails) {
      return <PauseCircleOutlineIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
    } else if (groupStatus.isActive) {
      return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
    } else if (groupStatus.isPartial) {
      return <RemoveCircleIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
    } else {
      return <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
    }
  }

  const GroupWrapper = isRoot ? Box : GroupItemContainer

  return (
    <GroupWrapper sx={{ ml: level * 2, mb: 1 }}>
      <Box
        ref={containerRef}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Group Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            backgroundColor: theme.palette.background.custom3 || '#222',
            borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {liveStatus && (
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>{renderStatusIcon()}</Box>
          )}

          {canViewPatternDetails ? (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                {group.groupTitle || 'Unnamed Group'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {symbol} {group.periodicity} {getTimeframeConversion(group.timeframe)} |{' '}
                {group.count} of {group.conditions.length + group.conditionGroups.length} required
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                Upgrade to view
              </Typography>
            </Box>
          )}

          <IconButton
            size="small"
            sx={{ color: theme.palette.text.secondary }}
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Group Content */}
        <Collapse in={expanded} onEntered={() => revealExpanded(containerRef.current)}>
          <Box sx={{ p: 1 }}>
            {/* Render conditions */}
            {group.conditions.length > 0 && (
              <ConditionContainer>
                {group.conditions.map((condition) => {
                  // Create hashable condition for status lookup
                  const hashableCondition = {
                    chartDetails: condition.chartDetails,
                    settings: condition.settings,
                    condition: condition.condition,
                  }

                  // Generate hash for condition status lookup
                  const conditionHash = murmurhash
                    .v3(JSON.stringify(sortObjectKeys(hashableCondition)))
                    .toString()

                  return (
                    <ConditionItem
                      key={condition.id}
                      condition={condition}
                      isActive={!!statuses[conditionHash]}
                      alertActive={alertActive}
                      liveStatus={liveStatus}
                      canViewPatternDetails={canViewPatternDetails}
                    />
                  )
                })}
              </ConditionContainer>
            )}

            {/* Render nested groups */}
            {group.conditionGroups.length > 0 && (
              <GroupContainer>
                {group.conditionGroups.map((nestedGroup) => (
                  <ConditionGroupItem
                    symbol={symbol}
                    key={nestedGroup.id}
                    group={nestedGroup}
                    statuses={statuses}
                    level={0} // Reset level for proper indentation within container
                    alertActive={alertActive}
                    liveStatus={liveStatus}
                    canViewPatternDetails={canViewPatternDetails}
                  />
                ))}
              </GroupContainer>
            )}
          </Box>
        </Collapse>
      </Box>
    </GroupWrapper>
  )
}

// Component to render condition group status header
export interface ConditionGroupStatusHeaderProps {
  isActive?: boolean
  title: string
  color: string
  statusCount?: number
  totalCount?: number
  percentage?: number
  liveStatus: boolean
  compact?: boolean
  children?: React.ReactNode
}

export const ConditionGroupStatusHeader: React.FC<ConditionGroupStatusHeaderProps> = ({
  isActive,
  title,
  color,
  statusCount,
  totalCount,
  percentage,
  liveStatus,
  compact = false,
  children,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <Box
      ref={containerRef}
      sx={{
        mb: 2,
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header with Progress Bar */}
      <Box
        sx={{
          position: 'relative',
          cursor: 'pointer',
          backgroundColor: theme.palette.background.custom3 || '#222',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Progress bar as background */}
        {liveStatus && isActive && percentage !== undefined && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${percentage}%`,
              backgroundColor: color,
              opacity: 0.1,
              zIndex: 0,
            }}
          />
        )}

        {/* Header content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: compact ? 0.75 : 1.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            variant={compact ? 'caption' : 'subtitle1'}
            sx={{
              fontWeight: 'medium',
              color: color,
            }}
          >
            {title}
          </Typography>

          {liveStatus && isActive && statusCount !== undefined && totalCount !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({statusCount}/{totalCount} met)
            </Typography>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            size="small"
            sx={{ color: theme.palette.text.secondary }}
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={expanded} onEntered={() => revealExpanded(containerRef.current)}>
        <Box sx={{ p: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  )
}

const PatternConditionReadView: React.FC<PatternConditionReadViewProps> = ({
  pattern,
  isActive = false,
  liveStatus = true,
}) => {
  const theme = useTheme()

  const { usermeta, canViewPrivatePatterns } = useApplicationSuiteContext()

  const { copyPattern } = useCopyPatternToAccount()

  // Use the custom hook for real-time status updates - only when liveStatus is true
  const {
    buyStatusCount,
    buyTotalCount,
    sellStatusCount,
    sellTotalCount,
    isLoading,
    buyPercentage,
    sellPercentage,
    buyStatuses,
    sellStatuses,
  } = useAlertStatus(liveStatus ? pattern : undefined, liveStatus ? isActive : false)

  const handleCopyPattern = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!pattern) return
    await copyPattern(pattern)
  }

  const canViewPatternDetails = canViewPrivatePatterns || isPatternOwner(pattern, usermeta)

  if (liveStatus && isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading conditions...</Typography>
      </Box>
    )
  }

  if (!pattern) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No pattern available</Typography>
      </Box>
    )
  }

  const buyConditionGroups = pattern.buyConditionGroups || []
  const sellConditionGroups = pattern.sellConditionGroups || []

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        {canViewPrivatePatterns && usermeta.email.split('@')[0] !== pattern.author && (
          <Button
            fullWidth
            sx={{ mb: 2 }}
            variant="outlined"
            onClick={handleCopyPattern}
            endIcon={<FileCopyIcon />}
          >
            Copy Pattern
          </Button>
        )}

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">Trigger:</Typography>

          <TriggerChartDetailsInput trigger={pattern.trigger} readOnly />
        </Box>
      </Box>

      {/* Buy Conditions Section */}
      {buyConditionGroups.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ConditionGroupStatusHeader
            isActive={isActive}
            title="Buy Conditions"
            color={theme.palette.success.main}
            statusCount={buyStatusCount}
            totalCount={buyTotalCount}
            percentage={buyPercentage}
            liveStatus={liveStatus}
          >
            {buyConditionGroups.map((group) => (
              <ConditionGroupItem
                symbol={pattern.trigger.chartDetails.symbol}
                key={group.id}
                group={group}
                statuses={buyStatuses}
                alertActive={isActive}
                liveStatus={liveStatus}
                isRoot={true}
                canViewPatternDetails={canViewPatternDetails}
              />
            ))}
          </ConditionGroupStatusHeader>
        </Box>
      )}

      {/* Sell Conditions Section */}
      {sellConditionGroups.length > 0 && (
        <Box>
          <ConditionGroupStatusHeader
            isActive={isActive}
            title="Sell Conditions"
            color={theme.palette.error.main}
            statusCount={sellStatusCount}
            totalCount={sellTotalCount}
            percentage={sellPercentage}
            liveStatus={liveStatus}
          >
            {sellConditionGroups.map((group) => (
              <ConditionGroupItem
                symbol={pattern.trigger.chartDetails.symbol}
                key={group.id}
                group={group}
                statuses={sellStatuses}
                alertActive={isActive}
                liveStatus={liveStatus}
                isRoot={true}
                canViewPatternDetails={canViewPatternDetails}
              />
            ))}
          </ConditionGroupStatusHeader>
        </Box>
      )}
    </Box>
  )
}

export default PatternConditionReadView
